import {
  AssociateRiskResponse,
} from '../../../types/Manager/atRiskAssociates.types';

type Props = {
  data: AssociateRiskResponse[];
  error?: string | null;
};

const getRiskColor = (risk: string) => {
  switch (risk.toUpperCase()) {
    case 'CRITICAL':
      return 'bg-red-100 text-red-700 border border-red-200';

    case 'AT RISK':
      return 'bg-orange-100 text-orange-700 border border-orange-200';

    default:
      return 'bg-yellow-100 text-yellow-700 border border-yellow-200';
  }
};

const getRowColor = (risk: string) => {
  switch (risk.toUpperCase()) {
    case 'CRITICAL':
      return 'bg-red-50';

    case 'AT RISK':
      return 'bg-orange-50';

    default:
      return 'bg-yellow-50';
  }
};

const getIdleColor = (idle: number) => {
  if (idle >= 25) {
    return 'bg-red-100 text-red-700';
  }

  if (idle >= 15) {
    return 'bg-orange-100 text-orange-700';
  }

  return 'bg-green-100 text-green-700';
};

const getProductivityColor = (value: number) => {
  if (value < 70) {
    return 'text-red-600';
  }

  if (value < 80) {
    return 'text-orange-600';
  }

  return 'text-green-600';
};

const getActionStatusColor = (status: string) => {
  switch (status.toUpperCase()) {
    case 'COMPLETED':
      return 'bg-green-100 text-green-700';

    case 'IN PROGRESS':
      return 'bg-blue-100 text-blue-700';

    case 'PENDING':
      return 'bg-yellow-100 text-yellow-700';

    default:
      return 'bg-slate-100 text-slate-600';
  }
};

const AtRiskAssociateRegister = ({
  data,
  error,
}: Props) => {

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-white p-6">
        <div className="flex h-72 items-center justify-center">
          <p className="text-sm font-medium text-red-600">
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">

      {/* Header */}

      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">

        <div className="flex items-center gap-3">

          <h2 className="text-[15px] font-semibold text-[#003E7E]">
            At-Risk Associate Register
          </h2>

          <span className="rounded-full bg-blue-50 px-3 py-1 text-[10px] font-medium text-blue-700">
            {data.length} Flagged Associates
          </span>

        </div>

      </div>

      {/* Table */}

      <div className="max-h-[430px] overflow-auto">

        <table className="min-w-full border-separate border-spacing-0 text-[10px] leading-tight">

          <thead className="sticky top-0 z-20 bg-slate-100">

            <tr>

              <th className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">
                Associate
              </th>

              <th className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">
                Team Leader
              </th>

              <th className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">
                Shift
              </th>

              <th className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">
                Location
              </th>

              <th className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-center font-semibold text-slate-700">
                Active Hrs
              </th>

              <th className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-center font-semibold text-slate-700">
                Productivity
              </th>

              <th className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-center font-semibold text-slate-700">
                Idle %
              </th>

              <th className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">
                Risk Flags
              </th>

              <th className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-center font-semibold text-slate-700">
                Risk Level
              </th>

              <th className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-center font-semibold text-slate-700">
                Weeks
              </th>

              <th className="whitespace-nowrap border-b border-slate-200 px-3 py-2 text-center font-semibold text-slate-700">
                Action
              </th>

            </tr>

          </thead>

          <tbody>
                        {data.map((row) => (

              <tr
                key={row.employeeCode}
                className={`${getRowColor(
                  row.riskLevel
                )} border-b border-slate-100 hover:bg-slate-50 transition-colors h-8`}
              >

                {/* Associate */}

                <td className="px-3 py-1 font-medium whitespace-nowrap text-slate-800">
                  {row.employeeName}
                </td>

                {/* Team Leader */}

                <td className="px-3 py-1 whitespace-nowrap text-slate-600">
                  {row.teamLeadName}
                </td>

                {/* Shift */}

                <td className="px-3 py-1 whitespace-nowrap text-slate-600">
                  {row.shiftName}
                </td>

                {/* Location */}

                <td className="px-3 py-1 whitespace-nowrap text-slate-600">
                  {row.city}
                </td>

                {/* Active Hours */}

                <td className="px-3 py-1 text-center font-medium text-slate-700">
                  {row.activeHours.toFixed(1)}
                </td>

                {/* Productivity */}

                <td
                  className={`px-3 py-1 text-center font-semibold whitespace-nowrap ${getProductivityColor(
                    row.productivity
                  )}`}
                >
                  {row.productivity.toFixed(1)}%
                </td>

                {/* Idle % */}

                <td className="px-3 py-1 text-center">

                  <span
                    className={`inline-flex items-center justify-center rounded-full px-2 py-[1px] text-[9px] font-semibold ${getIdleColor(
                      row.idlePercent
                    )}`}
                  >
                    {row.idlePercent.toFixed(1)}%
                  </span>

                </td>

                                {/* Risk Flags */}

                <td className="px-3 py-1">

                  <div className="flex flex-wrap gap-1">

                    {row.riskFlags
                      .split(',')
                      .filter(flag => flag.trim() !== '')
                      .map(flag => (

                        <span
                          key={flag}
                          className="rounded-full bg-pink-50 border border-pink-100 px-1.5 py-[1px] text-[8px] font-medium text-pink-700 whitespace-nowrap"
                        >
                          {flag.trim()}
                        </span>

                      ))}

                  </div>

                </td>

                {/* Risk Level */}

                <td className="px-3 py-1 text-center">

                  <span
                    className={`inline-flex items-center justify-center rounded-full px-2 py-[1px] text-[9px] font-semibold whitespace-nowrap ${getRiskColor(
                      row.riskLevel
                    )}`}
                  >
                    {row.riskLevel}
                  </span>

                </td>

                {/* Weeks at Risk */}

                <td className="px-3 py-1 text-center text-slate-700 whitespace-nowrap">

                  {'weeksAtRisk' in row
                    ? `${(row as any).weeksAtRisk} wk`
                    : '-'}

                </td>

                {/* Action Status */}

                <td className="px-3 py-1 text-center">

                  <span
                    className={`inline-flex items-center justify-center rounded-full px-2 py-[1px] text-[8px] font-semibold whitespace-nowrap ${getActionStatusColor(
                      (row as any).actionStatus ?? ''
                    )}`}
                  >
                    {(row as any).actionStatus ?? 'N/A'}
                  </span>

                </td>

              </tr>

            ))}
                        {/* Empty State */}

            {data.length === 0 && (

              <tr>

                <td
                  colSpan={11}
                  className="px-4 py-8 text-center"
                >

                  <div className="flex flex-col items-center justify-center gap-2">

                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100">

                      <svg
                        className="h-5 w-5 text-slate-400"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 17v-2a4 4 0 014-4h7m0 0l-3-3m3 3l-3 3M5 7h10"
                        />
                      </svg>

                    </div>

                    <p className="text-sm font-medium text-slate-600">
                      No at-risk associates found
                    </p>

                    <p className="text-xs text-slate-400">
                      There are no flagged associates for the selected period.
                    </p>

                  </div>

                </td>

              </tr>

            )}

          </tbody>

        </table>

      </div>

    </div>

  );

};

export default AtRiskAssociateRegister;